import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  examTypesApi,
  servicesApi,
  type CnpjDTO,
  type ExamTypeDTO,
  type ServiceDTO,
} from './api';

export const servicesKeys = {
  cnpjs: ['cnpjs'] as const,
  all: (cnpjId: string | null) => ['services', cnpjId] as const,
  detail: (id: string) => ['services', 'detail', id] as const,
  examTypes: (serviceId: string) => ['exam-types', serviceId] as const,
};

/**
 * Recupera o CNPJ ativo do usuário. Em ausência de CnpjContext global
 * (será implementado em tarefa posterior), usamos o CNPJ marcado
 * como `isDefault` — onboarding garante exatamente 1 default.
 */
export function useActiveCnpjId(): {
  cnpjId: string | null;
  isLoading: boolean;
  cnpjs: CnpjDTO[];
} {
  const q = useQuery({
    queryKey: servicesKeys.cnpjs,
    queryFn: () => servicesApi.listCnpjs(),
    staleTime: 60_000,
  });
  const cnpjs = q.data ?? [];
  const active = cnpjs.find((c) => c.isDefault) ?? cnpjs[0] ?? null;
  return { cnpjId: active?.id ?? null, isLoading: q.isLoading, cnpjs };
}

export function useServices(cnpjId: string | null) {
  return useQuery({
    queryKey: servicesKeys.all(cnpjId),
    queryFn: () => servicesApi.list(cnpjId!),
    enabled: !!cnpjId,
  });
}

export function useServiceMutations(cnpjId: string | null) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: servicesKeys.all(cnpjId) });

  const create = useMutation({
    mutationFn: (payload: Record<string, unknown>) => servicesApi.create(payload),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      servicesApi.update(id, payload),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => servicesApi.remove(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: servicesKeys.all(cnpjId) });
      const previous = qc.getQueryData<ServiceDTO[]>(servicesKeys.all(cnpjId));
      qc.setQueryData<ServiceDTO[]>(servicesKeys.all(cnpjId), (old) =>
        (old ?? []).filter((s) => s.id !== id),
      );
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(servicesKeys.all(cnpjId), ctx.previous);
    },
    onSettled: invalidate,
  });

  const restore = useMutation({
    mutationFn: (payload: Record<string, unknown>) => servicesApi.restore(payload),
    onSuccess: invalidate,
  });

  const reorder = useMutation({
    mutationFn: ({ id, newDisplayOrder }: { id: string; newDisplayOrder: number }) =>
      servicesApi.reorder(id, newDisplayOrder),
    onSuccess: invalidate,
  });

  return { create, update, remove, restore, reorder };
}

export function useExamTypes(serviceId: string | null) {
  return useQuery({
    queryKey: serviceId ? servicesKeys.examTypes(serviceId) : ['exam-types', 'noop'],
    queryFn: () => examTypesApi.list(serviceId!),
    enabled: !!serviceId,
  });
}

export function useExamTypeMutations(serviceId: string | null) {
  const qc = useQueryClient();
  const invalidate = () =>
    serviceId && qc.invalidateQueries({ queryKey: servicesKeys.examTypes(serviceId) });

  const create = useMutation({
    mutationFn: (payload: { name: string; shortName: string; value: number }) =>
      examTypesApi.create(serviceId!, payload),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Partial<{ name: string; shortName: string; value: number }>;
    }) => examTypesApi.update(id, payload),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => examTypesApi.remove(id),
    onSuccess: invalidate,
  });

  const reorder = useMutation({
    mutationFn: ({ id, newDisplayOrder }: { id: string; newDisplayOrder: number }) =>
      examTypesApi.reorder(id, newDisplayOrder),
    // Optimistic update: reordena no cache antes do round-trip para feedback fluido.
    onMutate: async ({ id, newDisplayOrder }) => {
      if (!serviceId) return;
      const key = servicesKeys.examTypes(serviceId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<ExamTypeDTO[]>(key);
      if (previous) {
        const current = previous.findIndex((e) => e.id === id);
        if (current >= 0) {
          const next = [...previous];
          const [moved] = next.splice(current, 1);
          const target = Math.max(0, Math.min(newDisplayOrder, next.length));
          next.splice(target, 0, moved);
          qc.setQueryData<ExamTypeDTO[]>(
            key,
            next.map((e, idx) => ({ ...e, displayOrder: idx })),
          );
        }
      }
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (serviceId && ctx?.previous) {
        qc.setQueryData(servicesKeys.examTypes(serviceId), ctx.previous);
      }
    },
    onSettled: invalidate,
  });

  return { create, update, remove, reorder };
}
